import { useState } from "react";
import { LoadingButton, Stack } from "@altimateai/lego";
import { DatamatesTracking } from "@/lib/tracking/datamates";
import { Badge, Button, toast, Tooltip } from "@altimateai/lego";
import { DatamateTemplateType } from "./constants";
import { cn } from "@/lib/utils";
import { Link, useNavigate } from "react-router-dom";
import Role from "./create/Role";
import Guardrails from "./create/Guardrails";
import Review from "./create/Review";
import Knowledge from "./create/Knowledge";
import { useMutation } from "@tanstack/react-query";
import { createDatamate, editDatamate } from "@/helpers/apis";
import useMCPContext from "./useMCPContext";
import { Teammate, updateTeammate } from "./mcpSlice";
import useTeammates from "./useTeammates";
import { BackButton } from "@/components/button";
import useIntegrations from "../teammate/useIntegrations";
import { useDatamateStepValidation } from "./useDatamateStepValidation";
import { useBreadcrumb } from "@/components/breadcrumbs";

export interface DatamateFormData {
  name: string;
  purpose: string;
  template?: DatamateTemplateType;
}

const CreateDatamate = ({ startingStep }: { startingStep: number }) => {
  const [currentStep, setCurrentStep] = useState(startingStep);
  const { teammate } = useMCPContext();
  const { refetch } = useTeammates();
  const validation = useDatamateStepValidation(currentStep, teammate);

  useBreadcrumb(teammate?.id ? "Edit" : "Create", {
    defaultParents: [
      { label: "Datamates", pathname: "/datamates" },
      ...(teammate?.id
        ? [{ label: teammate.name || "Details", pathname: `/datamate/${teammate.id}` }]
        : []),
    ],
  });

  const updateStep = (step: number) => {
    DatamatesTracking.wizardStepChanged(currentStep, step);
    setCurrentStep(step);
  };

  const totalSteps = [
    { label: "Define", component: () => <Role updateStep={updateStep} /> },
    { label: "Integrations", component: () => <Knowledge /> },
    { label: "Guardrails", component: () => <Guardrails /> },
    { label: "Overview", component: () => <Review updateStep={updateStep} /> },
  ];

  useIntegrations();

  const navigate = useNavigate();
  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async () => {
      if (!teammate) {
        throw new Error("Teammate not found");
      }

      if (teammate.id) {
        await editDatamate(teammate.id, teammate);
        return;
      }
      await createDatamate(teammate);
    },
    onSuccess: async () => {
      if (teammate?.id) {
        DatamatesTracking.edited(teammate.id);
      } else {
        DatamatesTracking.created(
          teammate?.integrations?.length ?? 0,
          !!teammate?.knowledge_bases?.length,
          teammate?.privacy ?? "private",
        );
      }
      toast({
        title:
          "Teammate saved successfully. If you are using vscode/cursor, please reload the editor to see the changes. If you are running datamate in terminal, please restart the datamate.",
      });
      const newTeammate = await refetch();
      const currentTeammates = (newTeammate.data?.datamates as Teammate[]) || [];
      const newTeammateId = currentTeammates.find(t => t.name === teammate?.name)?.id;
      if (newTeammateId) {
        navigate(`/datamate/${newTeammateId}`);
      }
      handleReset();
    },
    onError: (err: unknown) => {
      DatamatesTracking.createFailed((err as Error)?.message || String(err));
      toast({
        title: `Error saving selected integrations: ${(err as Error)?.message || err}`,
        variant: "destructive",
      });
    },
  });

  const handleReset = () => {
    updateTeammate({
      name: "",
      description: "",
      integrations: [],
      interfaces: [],
      knowledge_bases: [],
      privacy: "private",
    });
  };

  const handleCreateDatamate = () => {
    mutate();
  };

  return (
    <Stack direction="column" className="al-p-0 al-w-full al-max-w-[1200px] al-mx-auto">
      {/* Header with step indicator */}
      <Stack direction="row" className="al-mb-4 al-justify-between al-items-center">
        <Stack align="center">
          <BackButton defaultUrl="/datamates" />
          <h1 className="al-text-2xl al-font-semibold al-text-[#1A1A1A]">
            {totalSteps[currentStep - 1].label}
          </h1>
        </Stack>

        <Stack direction="row" className="al-items-center al-gap-2">
          {Array.from({ length: totalSteps.length }).map((_, index) => {
            const isActive = index + 1 === currentStep;
            return (
              <Stack key={index} direction="row" className="al-items-center al-gap-2">
                {index !== 0 && (
                  <div
                    className={cn(
                      "al-w-8 al-h-1 al-rounded",
                      isActive ? "al-bg-[#0066FF]" : "al-bg-[#E5E5E5]"
                    )}
                  />
                )}
                <Badge
                  className={cn("al-text-sm")}
                  variant={isActive ? "default" : "secondary"}
                  aria-current={isActive ? "step" : undefined}
                >
                  {totalSteps[index].label}
                </Badge>
              </Stack>
            );
          })}
        </Stack>
      </Stack>

      {/* Step content */}
      <Stack direction="column">{totalSteps[currentStep - 1].component()}</Stack>

      {/* Navigation buttons */}
      <Stack direction="row" className="al-justify-end">
        {currentStep > 1 && currentStep < totalSteps.length ? (
          <Button variant="secondary" onClick={() => setCurrentStep(currentStep - 1)}>
            Back
          </Button>
        ) : (
          <Button variant="secondary" asChild>
            <Link to="/datamates">Cancel</Link>
          </Button>
        )}

        {currentStep === totalSteps.length ? (
          <LoadingButton variant="default" onClick={handleCreateDatamate} loading={isLoading}>
            {teammate?.id ? "Update Datamate" : "Create Datamate"}
          </LoadingButton>
        ) : (
          <Tooltip title={!validation.isValid ? validation.message : undefined}>
            <span>
              <Button
                variant="default"
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!validation.isValid}
                className={cn(!validation.isValid && "al-cursor-not-allowed")}
              >
                Next: {totalSteps[currentStep].label}
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};

export default CreateDatamate;
